import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Globals } from 'src/app/common/globals';
import { firstValueFrom } from 'rxjs';
import { CommonService } from 'src/app/common/services/common.service';
import { CryptoService } from 'src/app/common/services/crypto.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  constructor(
    private http: HttpClient,
    private globals: Globals,
    private commonService: CommonService,
    private cryptoService: CryptoService
  ) { }

  checkLoginCredentials(payload: {
    email: string;
    password: string;
    remember?: boolean;
  }) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(this.globals.baseAPIUrl + '/login/verify', payload)
      ).then(
        async (res: any) => {
          const decryptedData = await this.cryptoService.decrypt(res?.data);
          const responseData = await JSON.parse(decryptedData);

          if (
            res?.code === 200 &&
            responseData?.access_token &&
            responseData?.user_data
          ) {
            const userData = responseData.user_data;


            let expiresMinutes = responseData.session_expire;
            if (!expiresMinutes) {
              expiresMinutes = 1440; // default: 1 day
            }

            // Handle cookies
            this.commonService.clearLoginCookie();
            this.commonService.setCookie(
              'access_token',
              responseData.access_token
            );
            this.commonService.setCookie('token_type', responseData.token_type);
            this.commonService.setCookie('logged_in_user_info', userData);

            const defaultRole = userData.roles[0];
            this.commonService.setCookie('current_role', defaultRole);

            // Apply remember me logic
            if (payload.remember) {
              this.commonService.setCookie('remember_me', true, expiresMinutes);
            }

            this.commonService.setSamlCookie('saml_access_token', responseData.saml_access_token);
            this.commonService.setSamlCookie('session_expire_minutes', responseData.session_expire_minutes);
            this.commonService.setSamlCookie('web_login_token', userData.id, responseData.session_expire_minutes);

            // Sync state to global
            this.commonService.syncAuthGlobals();

            if (
              this.globals.isCandidate &&
              !this.globals.isItAdmin &&
              !this.globals.isAdmin &&
              !this.globals.isAuditor &&
              !this.globals.isCredentialManager &&
              !this.globals.isAccountant &&
              !this.globals.isFacility
            ) {

              this.globals.showWelcomeNote = true;
            }

            resolve({ ...res, data: responseData });
          } else {
            reject(res);
          }
        },
        (error) => {
          // Handle network or server failure
          const message =
            error?.error?.message ||
            'OTP verification failed. Please try again.';

          reject(error);
        }
      );
    });
  }

  checkLoginCredentialsWithoutOTP(payload: any) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(this.globals.baseAPIUrl + '/login-without-otp', payload)
      ).then(
        async (res: any) => {
          const decryptedData = await this.cryptoService.decrypt(res?.data);
          const responseData = await JSON.parse(decryptedData);

          if (
            res?.code === 200 &&
            responseData?.access_token &&
            responseData?.user_data
          ) {
            const userData = responseData.user_data;


            let expiresMinutes = responseData.session_expire;
            if (!expiresMinutes) {
              expiresMinutes = 1440; // default: 1 day
            }

            // Handle cookies
            this.commonService.clearLoginCookie();
            this.commonService.setCookie(
              'access_token',
              responseData.access_token
            );
            this.commonService.setCookie('token_type', responseData.token_type);
            this.commonService.setCookie('logged_in_user_info', userData);

            const defaultRole = userData.roles[0];
            this.commonService.setCookie('current_role', defaultRole);

            // Apply remember me logic
            if (payload.remember) {
              this.commonService.setCookie('remember_me', true, expiresMinutes);
            }

            this.commonService.setSamlCookie('saml_access_token', responseData.saml_access_token);
            this.commonService.setSamlCookie('session_expire_minutes', responseData.session_expire_minutes);
            this.commonService.setSamlCookie('web_login_token', userData.id, responseData.session_expire_minutes);

            // Sync state to global
            this.commonService.syncAuthGlobals();

            if (
              this.globals.isCandidate &&
              !this.globals.isItAdmin &&
              !this.globals.isAdmin &&
              !this.globals.isAuditor &&
              !this.globals.isCredentialManager &&
              !this.globals.isAccountant &&
              !this.globals.isFacility
            ) {

              this.globals.showWelcomeNote = true;
            }

            resolve({ ...res, data: responseData });
          } else {
            reject(res);
          }
        },
        (error) => {
          // Handle network or server failure
          const message =
            error?.error?.message ||
            'OTP verification failed. Please try again.';

          reject(error);
        }
      );
    });
  }

  sendLoginOtp(payload: { email: string; password: string }) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.post(this.globals.baseAPIUrl + '/login', payload)
      ).then(
        (res: any) => {
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }

  logout() {
    return new Promise((resolve, reject) => {
      firstValueFrom(this.http.get(this.globals.baseAPIUrl + '/logout')).then(
        (res: any) => {
          this.globals.authData = null;
          this.globals.isLoggedIn = false;
          this.commonService.clearLoginCookie();
          resolve(res);
        },
        (msg) => {
          this.globals.authData = null;
          this.globals.isLoggedIn = false;
          this.commonService.clearLoginCookie();
          reject(msg);
        }
      );
    });
  }

  resendVerifyLink(id: string | null) {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.http.get(
          this.globals.baseAPIUrl + '/user/resend-verify-link/' + id
        )
      ).then(
        (res: any) => {
          resolve(res);
        },
        (msg) => {
          reject(msg);
        }
      );
    });
  }

}
